import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, CheckCircle2, Circle, RefreshCw, Target, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../../shared/apiClient';
import { useDefaultBoardId } from '../../../shared/hooks';

interface OnboardingStatus {
  bugTypeFieldConfigured: boolean;
  escapeRootCauseConfigured: boolean;
  statusTransitionsSynced: boolean;
  statusTransitionsCount: number;
}

const DISMISS_KEY = 'analytics.quality.onboardingBannerDismissed.v1';

interface Step {
  id: string;
  done: boolean;
  title: string;
  description: string;
  action: {
    kind: 'link';
    to: string;
    label: string;
  } | null;
  optional?: boolean;
}

/**
 * Quality tab onboarding checklist. Aggregates four "is this configured?"
 * signals (default board, status transitions synced, Bug Type field, Escape
 * root-cause field) into a single banner the user can tick off. Hides itself
 * automatically when every required step is satisfied, and stays dismissed
 * via `localStorage` once the user closes it.
 */
export const QualityOnboardingBanner: React.FC = () => {
  const boardId = useDefaultBoardId();
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/quality/onboarding-status');
        if (!res.ok) return;
        const data = (await res.json()) as OnboardingStatus;
        if (!cancelled) setStatus(data);
      } catch {
        // Silently ignore — banner is purely advisory.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const steps: Step[] = useMemo(
    () => [
      {
        id: 'board',
        done: !!boardId,
        title: 'Choose a default sprint board',
        description:
          'Dynamics needs a Jira board to pull sprint dates. Without this the Group by = Sprint charts stay empty.',
        action: { kind: 'link', to: '/releases#sprint-plan-section', label: 'Open Sprint Plan' },
      },
      {
        id: 'fullUpdate',
        done: !!status?.statusTransitionsSynced,
        title: 'Run a Full Update',
        description:
          'Pulls issue status history from Jira — powers MTTR, Reopens, VF, QA throughput and First-time pass.',
        action: {
          kind: 'link',
          to: '/analytics?tab=overview#analytics-database-anchor',
          label: 'Run Full Update',
        },
      },
      {
        id: 'bugType',
        done: !!status?.bugTypeFieldConfigured,
        title: 'Pick the Bug Type classifier field',
        description:
          'Optional. Enables the “By Bug Type” card (e.g. Production vs New feature vs Regression).',
        action: {
          kind: 'link',
          to: '/settings?tab=project&section=quality-settings',
          label: 'Configure in Settings',
        },
        optional: true,
      },
      {
        id: 'escapeCause',
        done: !!status?.escapeRootCauseConfigured,
        title: 'Pick the Escape root-cause field',
        description:
          'Optional. Enables the K · Escape taxonomy chart and the Escape root-cause table.',
        action: {
          kind: 'link',
          to: '/settings?tab=project&section=quality-settings',
          label: 'Configure in Settings',
        },
        optional: true,
      },
    ],
    [boardId, status]
  );

  const requiredSteps = steps.filter((s) => !s.optional);
  const pendingRequired = requiredSteps.filter((s) => !s.done);
  const allRequiredDone = pendingRequired.length === 0;

  // When everything required is ticked and the user hasn't already dismissed,
  // collapse to a compact congratulations line that can still be dismissed.
  if (dismissed) return null;
  if (!status) return null;

  if (allRequiredDone) {
    return (
      <div
        className="quality-onboarding-banner quality-onboarding-banner--all-set"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 14px',
          marginBottom: 16,
          borderRadius: 8,
          border: '1px solid rgba(34,197,94,0.4)',
          background: 'rgba(34,197,94,0.08)',
          color: 'var(--color-text)',
        }}
        role="status"
      >
        <CheckCircle2 size={16} color="#22c55e" />
        <span style={{ flex: 1, fontSize: 13 }}>
          Quality is set up. Optional fields: {steps[2].done ? 'Bug Type ✓' : 'Bug Type —'},{' '}
          {steps[3].done ? 'Escape cause ✓' : 'Escape cause —'}.
        </span>
        <button
          type="button"
          onClick={() => {
            try {
              localStorage.setItem(DISMISS_KEY, '1');
            } catch {
              /* ignore */
            }
            setDismissed(true);
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            background: 'transparent',
            border: 'none',
            color: 'inherit',
            cursor: 'pointer',
            fontSize: 12,
          }}
          aria-label="Dismiss onboarding banner"
        >
          <X size={14} /> dismiss
        </button>
      </div>
    );
  }

  return (
    <div
      className="quality-onboarding-banner"
      style={{
        padding: '14px 16px',
        marginBottom: 16,
        borderRadius: 8,
        border: '1px solid rgba(99,102,241,0.4)',
        background: 'rgba(99,102,241,0.08)',
      }}
      role="region"
      aria-label="Quality onboarding"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <h3
          style={{ margin: 0, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Target size={14} />
          Finish setting up Quality
          <span
            style={{
              padding: '2px 6px',
              borderRadius: 999,
              background: 'rgba(99,102,241,0.2)',
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
            }}
          >
            {pendingRequired.length} to go
          </span>
        </h3>
        <button
          type="button"
          onClick={() => {
            try {
              localStorage.setItem(DISMISS_KEY, '1');
            } catch {
              /* ignore */
            }
            setDismissed(true);
          }}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--color-text-secondary, #888)',
            cursor: 'pointer',
            fontSize: 12,
          }}
          aria-label="Dismiss onboarding banner"
          title="Dismiss. You can re-enable from Settings → Quality (TODO)."
        >
          <X size={14} />
        </button>
      </div>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
        {steps.map((step) => (
          <li
            key={step.id}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '8px 10px',
              borderRadius: 6,
              background: step.done ? 'rgba(34,197,94,0.08)' : 'transparent',
              border: `1px solid ${step.done ? 'rgba(34,197,94,0.25)' : 'transparent'}`,
            }}
          >
            <span style={{ marginTop: 2 }}>
              {step.done ? (
                <CheckCircle2 size={16} color="#22c55e" />
              ) : (
                <Circle size={16} color="var(--color-text-secondary, #888)" />
              )}
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {step.title}
                {step.optional && (
                  <span
                    style={{
                      marginLeft: 6,
                      fontSize: 10,
                      color: 'var(--color-text-secondary, #888)',
                      fontWeight: 400,
                    }}
                  >
                    (optional)
                  </span>
                )}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--color-text-secondary, #aaa)',
                  marginTop: 2,
                  lineHeight: 1.4,
                }}
              >
                {step.description}
              </div>
            </div>
            {!step.done && step.action?.kind === 'link' && (
              <Link
                to={step.action.to}
                className="btn btn-secondary btn-sm"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '4px 10px',
                  borderRadius: 4,
                  fontSize: 12,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {step.id === 'fullUpdate' ? <RefreshCw size={12} /> : null}
                {step.action.label}
                <ArrowRight size={12} />
              </Link>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};
